import { FC, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { AD_ANALYSIS_LIBRARY_ROUTE } from 'src/constants/routes'
import { useDeleteMonitoringPageMutation } from 'src/reactQuery/useDeleteMonitoringPageMutation'
import { SnapshotStatus } from 'src/reactQuery/useGetMonitoringPagesQuery'
import { CountryFlag } from 'src/components/CountryFlag'
import { Skeleton } from 'src/components/Skeleton'
import { ReactComponent as TrashIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/trash.svg'
import classes from './competitorCard.module.scss'

const MAX_VISIBLE_COUNTRIES = 3

// Previously .adsCountSkeleton / .chipSkeleton with a local skeletonPulse
// keyframe; same dimensions, now on the shared shimmer.
const ADS_COUNT_SKELETON_HEIGHT = 32
const ADS_COUNT_SKELETON_WIDTH = 48
const ADS_COUNT_SKELETON_RADIUS = 8
const CHIP_SKELETON_HEIGHT = 26
const CHIP_SKELETON_WIDTH = 84
const CHIP_SKELETON_RADIUS = 20

interface CompetitorCardProps {
  activeAds: number
  addedAt: string
  id: string
  lastSnapshotAt: string
  logo: string
  name: string
  pageId: string
  platforms: string[]
  snapshotStatus: SnapshotStatus | null
  targetCountries: { code?: string; name: string }[]
}

const formatSnapshotTime = (iso: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
}

export const CompetitorCard: FC<CompetitorCardProps> = ({
  activeAds,
  addedAt,
  id,
  lastSnapshotAt,
  logo,
  name,
  pageId,
  platforms,
  snapshotStatus,
  targetCountries,
}) => {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showAllCountries, setShowAllCountries] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const deleteMutation = useDeleteMonitoringPageMutation()

  const visibleCountries = showAllCountries
    ? targetCountries
    : targetCountries.slice(0, MAX_VISIBLE_COUNTRIES)

  // Only 'pending' is syncing — the list query polls on pending, so it will
  // resolve. A null/absent status with no snapshot just renders its (zero)
  // numbers rather than a skeleton that never clears.
  const syncing = snapshotStatus === 'pending'
  const snapshotFailed = snapshotStatus === 'failed'
  const snapshotTime = formatSnapshotTime(lastSnapshotAt)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.logoWrapper}>
          <img
            alt={name}
            className={classes.logo}
            onError={(e) => {
              const target = e.currentTarget
              target.style.display = 'none'
              const fallback = target.nextElementSibling as HTMLElement
              if (fallback) fallback.style.display = 'flex'
            }}
            src={logo}
          />
          <div className={classes.logoFallback} style={{ display: 'none' }}>
            {name.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className={classes.headerInfo}>
          <span className={classes.name}>{name}</span>
          {snapshotFailed && (
            <span
              className={classes.snapshotWarning}
              title="Last refresh failed — showing the most recent successful snapshot."
            >
              ⚠ Snapshot stale
            </span>
          )}
        </div>
        <div className={classes.menuWrapper} ref={menuRef}>
          <button
            className={classes.menuBtn}
            onClick={() => setMenuOpen((v) => !v)}
            type="button"
          >
            <svg
              fill="none"
              height="20"
              viewBox="0 0 20 20"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="10" cy="4" fill="#25262D" r="1.5" />
              <circle cx="10" cy="10" fill="#25262D" r="1.5" />
              <circle cx="10" cy="16" fill="#25262D" r="1.5" />
            </svg>
          </button>
          {menuOpen && (
            <div className={classes.menuDropdown}>
              <button
                className={classes.menuDeleteItem}
                onClick={() => {
                  deleteMutation.mutate(id)
                  setMenuOpen(false)
                }}
                type="button"
              >
                <TrashIcon className={classes.menuDeleteIcon} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={classes.divider} />

      <div className={classes.body}>
        <div className={classes.adsSection}>
          {syncing ? (
            <Skeleton
              height={ADS_COUNT_SKELETON_HEIGHT}
              radius={ADS_COUNT_SKELETON_RADIUS}
              width={ADS_COUNT_SKELETON_WIDTH}
            />
          ) : (
            <span className={classes.adsCount}>{activeAds}</span>
          )}
          <div className={classes.adsBottom}>
            <span className={classes.adsLabel}>
              {syncing ? 'Syncing…' : 'Active Ads'}
            </span>
          </div>
        </div>

        <div className={classes.infoSection}>
          <div className={classes.platformsSection}>
            <span className={classes.sectionLabel}>Platforms</span>
            <div className={classes.platformsList}>
              {syncing ? (
                <>
                  <Skeleton
                    height={CHIP_SKELETON_HEIGHT}
                    radius={CHIP_SKELETON_RADIUS}
                    width={CHIP_SKELETON_WIDTH}
                  />
                  <Skeleton
                    height={CHIP_SKELETON_HEIGHT}
                    radius={CHIP_SKELETON_RADIUS}
                    width={CHIP_SKELETON_WIDTH}
                  />
                </>
              ) : (
                <>
                  {platforms.includes('facebook') && (
                    <div className={classes.platformBadgeFacebook}>
                      <FacebookIcon className={classes.platformIcon} />
                      <span>Facebook</span>
                    </div>
                  )}
                  {platforms.includes('instagram') && (
                    <div className={classes.platformBadgeInstagram}>
                      <InstagramIcon className={classes.platformIcon} />
                      <span>Instagram</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={classes.countriesSection}>
            <span className={classes.sectionLabel}>Target Countries</span>
            <div className={classes.countriesList}>
              {targetCountries.length === 0 ? (
                <span className={classes.countriesEmpty}>—</span>
              ) : (
                <>
                  {visibleCountries.map((country) => (
                    <span className={classes.countryChip} key={country.name}>
                      <CountryFlag
                        className={classes.countryFlag}
                        code={country.code}
                        name={country.name}
                      />
                      {country.name}
                    </span>
                  ))}
                  {targetCountries.length > MAX_VISIBLE_COUNTRIES && (
                    <button
                      className={classes.countryChipMore}
                      onClick={() => setShowAllCountries((v) => !v)}
                      type="button"
                    >
                      {showAllCountries
                        ? 'Show less'
                        : `+${targetCountries.length - MAX_VISIBLE_COUNTRIES} more`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.timestamps}>
          {addedAt && <span>Added at {addedAt}</span>}
          {addedAt && snapshotTime && (
            <span className={classes.timestampSep}>|</span>
          )}
          {snapshotTime && <span>Updated · {snapshotTime}</span>}
        </div>
        <button
          className={classes.viewBtn}
          onClick={() =>
            navigate(`${AD_ANALYSIS_LIBRARY_ROUTE}/${pageId}`, {
              state: { pageName: name },
            })
          }
          type="button"
        >
          View Competitor →
        </button>
      </div>
    </div>
  )
}
